import {Injectable, Injector} from '@angular/core';

// RXJS
import { map, tap } from 'rxjs/operators';

// NGRX
import { Actions, createEffect, ofType } from '@ngrx/effects';

// Services
import { GuestService } from '../modules/core/services/guest.service';
import { BookingService } from '../modules/core/services/booking.service';
import { AnalyticsService } from '../modules/core/services/analytics.services';
import { GuestDetailsService } from '../modules/core/services/guest-details.service';

// Miscellaneous
import * as tracking from '../actions/tracking';
import { TelemetryGenericErrorInterface } from '../shared/interfaces';
import {TealiumService} from '../modules/core/services/tealium.service';
import {
  trackPagePayloadCase,
  TrackingUtilsService,
  trackOlciStartEventCase,
  trackGuestPhotoEventCase,
  trackOlciFlowPayloadCase,
  trackPrintSspPayloadCase,
  trackSaveCardPayloadCase,
  trackPayMethodPayloadCase,
  trackPageErrorPayloadCase,
  trackFieldErrorPayloadCase,
  trackInlineAlertPayloadCase,
  trackOlciCompletePayloadCase,
  trackBackendErrorPayloadCase,
  trackReadyToBoardPayloadCase,
  trackErrorPageViewPayloadCase,
  trackGlobalVariablesPayloadCase,
  trackPageInteractionPayloadCase,
  trackFrontendDeepLinkPayloadCase,
  trackCtcTermsPageViewPayloadCase,
  trackVisitHealthCenterPayloadCase,
  trackBackendUserErrorsPayloadCase,
  trackInputFieldSelectionPayloadCase,
  trackReplaceSavedCreditCardPayloadCase,
  trackExpenseAccountPayMethodPayloadCase,
  trackHealthCenterExternalLinkPayloadCase,
} from './tracking-cases';

declare let utag: any;

@Injectable()
export class TrackingEffects {
  private trackingUtilsService: TrackingUtilsService;
  private guestDetailsService: GuestDetailsService;
  private bookingService: BookingService;
  private guestService: GuestService;
  private analyticsService: AnalyticsService;
  private tealiumService: TealiumService;

  constructor(
    private readonly actions$: Actions,
    private readonly injector : Injector,
  ) {
    this.injectServices();
  }

  injectServices() {
    this.trackingUtilsService = this.injector.get<TrackingUtilsService>(TrackingUtilsService);
    this.guestDetailsService = this.injector.get<GuestDetailsService>(GuestDetailsService);
    this.bookingService = this.injector.get<BookingService>(BookingService);
    this.guestService = this.injector.get<GuestService>(GuestService);
    this.analyticsService = this.injector.get<AnalyticsService>(AnalyticsService);
    this.tealiumService = this.injector.get<TealiumService>(TealiumService);
  }

  get trackGlobalVariables() {
    return trackGlobalVariablesPayloadCase(this.trackingUtilsService);
  }

  // track_landingpage_view  on selectforcheckin page (landing-page.component.ts)
  trackPageView$ = createEffect(() =>
      this.actions$.pipe(
        ofType(tracking.TrackingActionTypes.TRACK_PAGE_VIEW),
        map(() => this.trackingUtilsService.getGuestData()),
        tap(guestData => {
          const utagPayload = trackPagePayloadCase(
            this.tealiumService,
            this.trackingUtilsService,
            guestData
          );
          if ((<any>window).utag && utagPayload) {
            utag.view(utagPayload);
          }
        })),
    { dispatch: false }
  );

  trackReadyToBoardView$ = createEffect(() => this.actions$.pipe(
      ofType(tracking.TrackingActionTypes.TRACK_READY_TO_BOARD_VIEW),
      map(() => this.trackingUtilsService.getGuestData()
      ),
      tap(guestData => {
        const utagPayload = trackReadyToBoardPayloadCase(this.analyticsService, this.trackingUtilsService, guestData);
        if ((<any>window).utag && utagPayload) {
          utag.view(utagPayload);
        }
      })),
    { dispatch: false }
  );

  trackVisitHealthCenterClick$ = createEffect(() => this.actions$.pipe(
      ofType(tracking.TrackingActionTypes.TRACK_VISIT_HEALTH_CENTER_CLICK),
      map(() =>  this.trackingUtilsService.getGuestData()
      ),
      tap(guestData => {
        const utagPayload = trackVisitHealthCenterPayloadCase(this.analyticsService, this.trackingUtilsService, guestData);
        if ((<any>window).utag && utagPayload) {
          utag.link(utagPayload);
        }
      })),
    { dispatch: false }
  );

  trackHealthCenterExternalLinkClick$ = createEffect(() => this.actions$.pipe(
      ofType(tracking.TrackingActionTypes.TRACK_HEALTH_CENTER_EXTERNAL_LINK_CLICK),
      map((action) => ({
          guestData : this.trackingUtilsService.getGuestData(),
          linkText : (action as any).payload.linkText
        })
      ),
      tap((payload: any) => {
        const utagPayload = trackHealthCenterExternalLinkPayloadCase(payload, this.analyticsService, this.trackingUtilsService);
        if ((<any>window).utag && utagPayload) {
          utag.link(utagPayload);
        }
      })),
    { dispatch: false }
  );

  // olciStart event to be dispatched with the new structure defined by analytics team
  trackOlciStartEvent$ = createEffect(() => this.actions$.pipe(
    ofType(tracking.TrackingActionTypes.TRACK_OLCI_START_EVENT),
    map(action => (action as any).payload),
    tap(payload => {
      if((<any>window).utag) {
        const response = trackOlciStartEventCase(payload, this.trackGlobalVariables);
        utag.link(response);
      }
    })),
    { dispatch: false }
  );

  // olciComplete event to be dispatched with the new structure defined by analytics team
  trackOlciCompleteEvent$ = createEffect(() => this.actions$.pipe(
      ofType(tracking.TrackingActionTypes.TRACK_OLCI_COMPLETE_EVENT),
      map(action => (action as any).payload),
      tap(payload => {
        if((<any>window).utag) {
          const response = trackOlciCompletePayloadCase(payload, this.trackGlobalVariables);
          utag.link(response);
        }
      })),
    { dispatch: false }
  );

  // saveCard event to be dispatched with the new structure defined by analytics team
  saveCardEvent$ = createEffect(() => this.actions$.pipe(
      ofType(tracking.TrackingActionTypes.TRACK_SAVE_CARD_EVENT),
      map(action => (action as any).payload),
      tap(payload => {
        if((<any>window).utag) {
          const response = trackSaveCardPayloadCase(payload, this.trackGlobalVariables);
          utag.link(response);
        }
      })),
    { dispatch: false }
  );

  // payMethod event to be dispatched with the new structure defined by analytics team
  payMethodEvent$ = createEffect(() => this.actions$.pipe(
      ofType(tracking.TrackingActionTypes.TRACK_PAY_METHOD_EVENT),
      map(action => (action as any).payload),
      tap(payload => {
        if((<any>window).utag) {
          const response = trackPayMethodPayloadCase(payload, this.trackGlobalVariables);
          utag.link(response);
        }
      })),
    { dispatch: false }
  );

  // guestPhoto event to be dispatched with the new structure defined by analytics team
  trackGuestPhotoEvent$ = createEffect(() => this.actions$.pipe(
    ofType(tracking.TrackingActionTypes.TRACK_GUEST_PHOTO_EVENT),
    map(action => (action as any).payload),
    tap(payload => {
      if((<any>window).utag) {
        const response = trackGuestPhotoEventCase(payload, this.trackGlobalVariables);
        utag.link(response);
      }
    })),
    { dispatch: false }
  )

  // DO NOT REFACTOR THIS CODE.
  // Analytics code is easier to use in the web app with small single-purpose effects,
  // as opposed to refactoring the effects for multiple use cases.

  // track_olci_flow_event for sailingClosed, luggageLink, addGuests, checkinLater, saveCard, updateCard, and olciComplete.
  trackOlciFlowEvent$ = createEffect(() => this.actions$.pipe(
      ofType(tracking.TrackingActionTypes.TRACK_OLCI_FLOW_EVENT),
      map(action => (action as any).payload),
      tap(payload => {
        if ((<any>window).utag) {
          const response = trackOlciFlowPayloadCase(
            payload,
            this.trackGlobalVariables,
            this.trackingUtilsService.getGuestData().loggedInPaxId
          );
          utag.link(response);
        }
      })),
    { dispatch: false }
  );

  // Expense Account Pay Method
  trackExpenseAccountPayMethodEvent$ = createEffect(() => this.actions$.pipe(
      ofType(tracking.TrackingActionTypes.TRACK_EXPENSE_ACCOUNT_PAY_METHOD),
      map(action => (action as any).payload),
      tap(payload => {
        if ((<any>window).utag) {
          const response = trackExpenseAccountPayMethodPayloadCase(
            payload,
            this.trackGlobalVariables
          );
          utag.link(response);
        }
      })),
    { dispatch: false }
  );

  TrackReplaceSavedCreditCardEvent$ = createEffect(() => this.actions$.pipe(
      ofType(tracking.TrackingActionTypes.TRACK_REPLACE_SAVED_CREDIT_CARD_EVENT),
      map(action => (action as any).payload),
      tap(payload => {
        if ((<any>window).utag) {
          const response = trackReplaceSavedCreditCardPayloadCase(
            payload,
            this.guestService.loggedInGuestPaxId,
            this.trackGlobalVariables
          );
          utag.link(response);
        }
      })),
    { dispatch: false }
  );

  // track_field_error_event  // input field errors only
  trackFieldErrorEvent$ = createEffect(() => this.actions$.pipe(
      ofType<any>(tracking.TrackingActionTypes.TRACK_FIELD_ERROR_EVENT),
      map(action => action.payload),
      tap(payload => {
        if ((<any>window).utag) {
          const response = trackFieldErrorPayloadCase(
            this.tealiumService.getCurrentPage(),
            payload,
            this.trackGlobalVariables
          );
          utag.link(response);
        }
      })),
    { dispatch: false }
  );

  // track_page_errors_event  // page errors from the back-end
  trackPageErrorEvent$ = createEffect(() => this.actions$.pipe(
      ofType<any>(tracking.TrackingActionTypes.TRACK_ERROR_PAGE_EVENT),
      map(action => action.payload),
      tap(payload => {
        if ((<any>window).utag) {
          const response = trackPageErrorPayloadCase(payload, this.trackGlobalVariables);
          utag.link(response);
        }
      })),
    { dispatch: false }
  );

  // track_inline_alerts_event
  trackInlineAlertEvent$ = createEffect(() => this.actions$.pipe(
    ofType(tracking.TrackingActionTypes.TRACK_INLINE_ALERT_EVENT),
    map(action => (action as any).payload),
    tap(payload => {
      if ((<any>window).utag) {
        const response = trackInlineAlertPayloadCase(this.removeBlankAttributes(payload), this.trackGlobalVariables);
        utag.link(response);
      }
    })),
{ dispatch: false }
  );

  // track_page_interactions_event
  trackPageInteractionEvent$ = createEffect(() => this.actions$.pipe(
      ofType<any>(tracking.TrackingActionTypes.TRACK_PAGE_INTERACTION_EVENT),
      map(action => action.payload),
      tap(payload => {
        if ((<any>window).utag) {
          const response = trackPageInteractionPayloadCase(
            typeof payload === 'object' ? this.removeBlankAttributes(payload) : payload,
            this.trackingUtilsService,
            this.trackGlobalVariables
          );
          utag.link(response);
        }
      })),
    { dispatch: false }
  );

  trackDeepLink$ = createEffect(
    () => this.actions$.pipe(
      ofType<any>(tracking.TrackingActionTypes.TRACK_FRONTEND_DEEP_LINK),
      map(action => action.payload),
      tap(payload => {
        if ((<any>window).utag) {
          const response = trackFrontendDeepLinkPayloadCase(payload, this.analyticsService, this.trackingUtilsService, this.trackGlobalVariables);
          utag.link(response);
        }
      })
    ),
    { dispatch: false }
  );

  // track_print_ssp_event
  trackPrintSSPEvent$ = createEffect(() => this.actions$.pipe(
      ofType<any>(tracking.TrackingActionTypes.TRACK_PRINT_SSP_EVENT),
      map(action => action.payload),
      tap(payload => {
        if ((<any>window).utag) {
          const response = trackPrintSspPayloadCase(
            payload,
            this.analyticsService,
            this.trackingUtilsService,
            this.trackGlobalVariables
          );
          utag.link(response);
        }
      })),
    { dispatch: false }
  );

  trackBackEndUserErrorsEvent$ = createEffect(() => this.actions$.pipe(
      ofType(tracking.TrackingActionTypes.TRACK_BACK_END_USER_ERRORS_EVENT),
      map(action => (action as any).payload),
      tap(payload => {
        if ((<any>window).utag) {
          const response = trackBackendUserErrorsPayloadCase(payload, this.trackGlobalVariables);
          utag.link(response);
        }
      })),
    { dispatch: false }
  );

  // track_error_page_event
  trackErrorPageViewEvent$ = createEffect(() => this.actions$.pipe(
      ofType(tracking.TrackingActionTypes.TRACK_ERROR_PAGE_VIEW_EVENT),
      tap(() => {
        if ((<any>window).utag) {
          const response = trackErrorPageViewPayloadCase(
            this.tealiumService.getCurrentPage(),
            this.trackGlobalVariables
          );
          utag.view(response);
        }
      })),
    { dispatch: false }
  );

  // track_ctc__term_page_event
  trackCTCTermsPageViewEvent$ = createEffect(() => this.actions$.pipe(
    ofType(tracking.TrackingActionTypes.TRACK_CTC_TERM_PAGE_LINK_CLICK),
    tap(() => {
      if ((<any>window).utag) {
        const response = trackCtcTermsPageViewPayloadCase(
          this.tealiumService.getCurrentPage(),
          this.trackGlobalVariables
        );
        utag.link(response);
      }
    })),
    { dispatch: false }
  );

  TrackGenericErrorAlertAction$ = createEffect(() => this.actions$.pipe(
    ofType(tracking.TrackingActionTypes.TRACK_GENERIC_ERROR_ALERT_EVENT),
    map(action => (action as any).payload),
    tap((payload: TelemetryGenericErrorInterface) => {
      if ((<any>window).utag) {
        utag.link({...payload});
      }
    })),
    { dispatch: false }
  );

  trackInputFieldSelection$ = createEffect(() => this.actions$.pipe(
    ofType(tracking.TrackingActionTypes.TRACK_INPUT_FIELD_SELECTION),
    map(action => (action as any).payload),
    tap(payload => {
      if ((<any>window).utag) {
        const response = trackInputFieldSelectionPayloadCase(
          payload,
          this.trackGlobalVariables
        );
        utag.link(response);
      }
    })),
    { dispatch: false }
  );

  trackBackendErrorEffect$ = createEffect(() => this.actions$.pipe(
    ofType(tracking.TrackingActionTypes.TRACK_TELEMETRY_BACKEND_ERROR),
    map(action => (action as any).payload),
    tap(payload => {
      if ((<any>window).utag) {
        const response = trackBackendErrorPayloadCase(
          payload,
          this.trackGlobalVariables
        );
        utag.link(response);
      }
    })),
  { dispatch: false });

  private removeBlankAttributes(obj: any): object {
    const result: any = {};
    for (const key in obj) {
        if (obj[key] !== null && obj[key] !== undefined && obj[key] !== '') {
            result[key] = obj[key];
        }
    }
    return result;
  }
}
